import {ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {BaseurlService} from '../../services/baseurl.service';
import {
  InsoDockerOverview,
  InsoDockerOverviewRow,
  InsoDockerPatchDay,
  InsoDockerPatchDayItem,
  InsoDockerPatchDayItemStatus,
  InsoDockerRule,
  InsoDockerRuleType,
  InsoDockerService,
  InsoDockerStatus
} from './inso-docker.service';

const FILTER_STATE_STORAGE_KEY = 'inso-docker-filter-state';

/** what the user filtered the overview down to, so that a reload does not throw it away */
interface InsoDockerFilterState {
  visibleStatus: Record<InsoDockerStatus, boolean>;
  /** planned is no status but it hides and shows the same way */
  showPlanned: boolean;
  /** neither is not reporting */
  showDown: boolean;
  /** the tenants that are switched off, so that one that turns up later is visible by default */
  hiddenTenants: string[];
  groupBy: InsoDockerGroupBy;
  search: string;
}

/** the tenants there are, a scan should never bring another one, but if it does it gets a button as well */
const KNOWN_TENANTS = ['tuw', 'fhwn', 'rit', 'tuw-dev'];

type InsoDockerGroupBy = 'none' | 'tenant' | 'vmPrefix' | 'tenantVmPrefix' | 'image';

/** what the plan form holds while the user fills it, the sane defaults come from the row that was clicked */
interface PlanDraft {
  image: string;
  tenant: string | null;
  vm: string | null;
  fromVersion: string | null;
  toVersion: string | null;
  comment: string | null;
  allVms: boolean;
  allTenants: boolean;
}

/** one image against the tenants, to see at a glance that gitlab is already patched at tuw but not at fhwn */
interface MatrixRow {
  image: string;
  newestVersion: string | null;
  cells: MatrixCell[];
}

interface MatrixCell {
  tenant: string;
  versions: MatrixVersion[];
}

/** one version of one image at one tenant, no matter on how many vms of that tenant it runs */
interface MatrixVersion {
  tag: string;
  vms: string[];
  cls: string;
  title: string;
  /** what a click plans, the first vm stands for the rest of them */
  row: InsoDockerOverviewRow;
}

interface OverviewGroup {
  name: string;
  rows: InsoDockerOverviewRow[];
  criticalCount: number;
  outdatedCount: number;
}

@Component({
  selector: 'app-inso-docker',
  imports: [
    DatePipe,
    FormsModule
  ],
  templateUrl: './inso-docker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './inso-docker.component.scss'
})
export class InsoDockerComponent implements OnInit, OnDestroy {

  overview: InsoDockerOverview | null = null;
  groups: OverviewGroup[] = [];
  matrix: MatrixRow[] = [];
  matrixTenants: string[] = [];
  /** the tenant buttons, the four we know plus whatever else a scan brings along */
  tenants: string[] = [...KNOWN_TENANTS];
  rules: InsoDockerRule[] = [];
  patchDays: InsoDockerPatchDay[] = [];
  patchDayItems: InsoDockerPatchDayItem[] = [];
  selectedPatchDayId: number | null = null;
  /** the items the user ticked, the ones a move takes along */
  selectedItems = new Set<number>();
  /** the patch day the move select points at, it falls back to its label as soon as the move is out */
  moveTargetId: number | null = null;

  live = true;
  sse: EventSource | null = null;
  upstreamRefreshing = false;

  groupBy: InsoDockerGroupBy = 'tenant';
  search = '';
  /** the noise the old script printed as well is hidden until somebody asks for it */
  visibleStatus: Record<InsoDockerStatus, boolean> = {
    CRITICAL: true,
    OUTDATED: true,
    UP_TO_DATE: false,
    LATEST: false,
    UNKNOWN: false,
    IGNORED: false,
  };
  /** planned rows bring their own switch, their status says little once somebody decided what to do about it */
  showPlanned = true;
  /** rows of a silent vm bring their own switch too, their status is the last known one and not a fact */
  showDown = true;

  plan: PlanDraft | null = null;
  newRule: InsoDockerRule | null = null;
  newPatchDay: InsoDockerPatchDay | null = null;
  showRules = false;

  /** the tenants the user switched off, everything not in here is shown */
  hiddenTenants = new Set<string>();

  readonly allStatus: InsoDockerStatus[] = ['CRITICAL', 'OUTDATED', 'UP_TO_DATE', 'LATEST', 'UNKNOWN', 'IGNORED'];
  readonly ruleTypes: InsoDockerRuleType[] = ['CRITICAL_BELOW', 'ABANDONED', 'IGNORE', 'LATEST_OVERRIDE'];

  /** requests that are still running, so that slow responses do not pile up while scans keep arriving */
  private overviewPending = false;

  constructor(
    private insoDockerService: InsoDockerService,
    private notificationService: ToastrService,
    private zone: NgZone,
    private baseUrlService: BaseurlService,
  ) { }

  ngOnInit(): void {
    this.loadFilterState();
    this.reload();
    this.setupSse();
  }

  ngOnDestroy(): void {
    if (this.sse) {
      this.sse.close();
    }
  }

  reload(): void {
    this.refreshOverview(true);
    this.refreshPatchDays(true);
    this.refreshRules(true);
  }

  toggleLive(): void {
    this.live = !this.live;
  }

  /**
   * Asks the backend to look up the newest versions right now instead of waiting for its hourly run. The lookups take
   * their time, so does the request. New versions arrive as an sse event anyway, the answer only ends the waiting.
   */
  refreshUpstream(): void {
    this.upstreamRefreshing = true;
    this.insoDockerService.refreshUpstream().subscribe({
      next: result => {
        this.upstreamRefreshing = false;
        this.notificationService.info(result.changed > 0
          ? result.changed + ' image(s) have a newer version now'
          : 'nothing new upstream');
      },
      error: err => {
        this.upstreamRefreshing = false;
        this.notificationService.error('the upstream check failed ' + JSON.stringify(err));
      }
    });
  }

  /**
   * Pulls the image of this row on the vm it runs on and looks at the containers there right after. Fire and
   * forget, the ansible run takes its time: what it said ends up in the stracli command response of the uid.
   */
  pull(row: InsoDockerOverviewRow): void {
    this.insoDockerService.pull({tenant: row.tenant, vm: row.vm, image: row.image, tag: row.tag}).subscribe({
      next: result => this.notificationService.info(`${row.image}:${row.tag} is being pulled on ${row.vm}, ${result.host} reports it as ${result.uid}`),
      error: err => this.notificationService.error('the pull could not be sent ' + JSON.stringify(err))
    });
  }

  // ── the overview ───────────────────────────────────────────────────────────

  toggleStatus(status: InsoDockerStatus): void {
    this.visibleStatus[status] = !this.visibleStatus[status];
    this.filterChanged();
  }

  togglePlanned(): void {
    this.showPlanned = !this.showPlanned;
    this.filterChanged();
  }

  toggleDown(): void {
    this.showDown = !this.showDown;
    this.filterChanged();
  }

  toggleTenant(tenant: string): void {
    if (this.hiddenTenants.has(tenant)) {
      this.hiddenTenants.delete(tenant);
    } else {
      this.hiddenTenants.add(tenant);
    }
    this.filterChanged();
  }

  /** everything the user filters by is kept, the next visit starts where the last one stopped */
  filterChanged(): void {
    this.saveFilterState();
    this.regroup();
  }

  private saveFilterState(): void {
    const state: InsoDockerFilterState = {
      visibleStatus: this.visibleStatus,
      showPlanned: this.showPlanned,
      showDown: this.showDown,
      hiddenTenants: Array.from(this.hiddenTenants),
      groupBy: this.groupBy,
      search: this.search,
    };
    localStorage.setItem(FILTER_STATE_STORAGE_KEY, JSON.stringify(state));
  }

  private loadFilterState(): void {
    const saved = localStorage.getItem(FILTER_STATE_STORAGE_KEY);
    if (!saved) {
      return;
    }
    try {
      const state: InsoDockerFilterState = JSON.parse(saved);
      // only the status the code still knows are taken over, an old state must not add or drop one
      this.allStatus.forEach(status => {
        const visible = state.visibleStatus?.[status];
        if (typeof visible === 'boolean') {
          this.visibleStatus[status] = visible;
        }
      });
      if (typeof state.showPlanned === 'boolean') {
        this.showPlanned = state.showPlanned;
      }
      if (typeof state.showDown === 'boolean') {
        this.showDown = state.showDown;
      }
      this.hiddenTenants = new Set(state.hiddenTenants ?? []);
      this.groupBy = state.groupBy ?? this.groupBy;
      this.search = state.search ?? this.search;
    } catch {
      localStorage.removeItem(FILTER_STATE_STORAGE_KEY);
    }
  }

  regroup(): void {
    this.rebuildTenants();
    const rows = (this.overview?.rows ?? []).filter(row => this.matchesFilter(row));
    const groups: Map<string, InsoDockerOverviewRow[]> = new Map<string, InsoDockerOverviewRow[]>();
    rows.forEach(row => {
      const name = this.groupNameOf(row);
      const group = groups.get(name);
      if (group) {
        group.push(row);
      } else {
        groups.set(name, [row]);
      }
    });
    this.groups = Array.from(groups.entries())
      .map(([name, groupRows]) => ({
        name,
        rows: groupRows,
        criticalCount: groupRows.filter(it => it.status === 'CRITICAL').length,
        outdatedCount: groupRows.filter(it => it.status === 'OUTDATED').length,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    this.rebuildMatrix();
  }

  // ── the matrix, images against tenants ─────────────────────────────────────

  /**
   * Builds the image times tenant table. The filters above work on it as well, but on whole rows and columns only:
   * a hidden tenant loses its column, and an image is gone once nothing of it is left to see in the columns that
   * remain. Inside an image that survives, every version stays, a green cell next to a red one is the point of it.
   */
  private rebuildMatrix(): void {
    const rows = (this.overview?.rows ?? []).filter(row => this.matchesSearch(row) && this.matchesTenant(row));
    this.matrixTenants = this.inTenantOrder(Array.from(new Set(rows.map(it => it.tenant))));

    const byImage = new Map<string, InsoDockerOverviewRow[]>();
    rows.forEach(row => {
      const imageRows = byImage.get(row.image);
      if (imageRows) {
        imageRows.push(row);
      } else {
        byImage.set(row.image, [row]);
      }
    });

    this.matrix = Array.from(byImage.entries())
      // an image whose every version is filtered away has nothing to say here, its whole row goes
      .filter(([, imageRows]) => imageRows.some(row => this.matchesFilter(row)))
      .map(([image, imageRows]) => ({
        image,
        newestVersion: imageRows.map(it => it.latestVersion).find(it => !!it) ?? null,
        cells: this.matrixTenants.map(tenant => this.matrixCell(tenant, imageRows.filter(it => it.tenant === tenant))),
      }))
      .sort((a, b) => a.image.localeCompare(b.image));
  }

  private matrixCell(tenant: string, rows: InsoDockerOverviewRow[]): MatrixCell {
    const byTag = new Map<string, InsoDockerOverviewRow[]>();
    rows.forEach(row => {
      const tagRows = byTag.get(row.tag);
      if (tagRows) {
        tagRows.push(row);
      } else {
        byTag.set(row.tag, [row]);
      }
    });

    const versions = Array.from(byTag.entries()).map(([tag, tagRows]) => {
      // as long as one vm of the tenant is not taken care of, the cell keeps the louder color of that one
      const worst = tagRows.find(it => !it.planned) ?? tagRows[0];
      const vms = tagRows.map(it => it.vm).sort((a, b) => a.localeCompare(b));
      const titleParts = [`${tag} on ${vms.join(', ')} · ${worst.status.toLowerCase()}`];
      if (worst.planned) {
        titleParts.push(this.plannedLabel(worst));
      }
      if (worst.latestVersion) {
        titleParts.push(`newest ${worst.latestVersion}`);
      }
      return {
        tag,
        vms,
        cls: this.rowClass(worst),
        title: titleParts.join(' · '),
        row: worst,
      };
    });

    versions.sort((a, b) => a.tag.localeCompare(b.tag));
    return {tenant, versions};
  }

  /** a click on a version chip plans that update, several vms behind it mean all of them at once */
  planFromMatrix(version: MatrixVersion): void {
    this.openPlan(version.row);
    if (this.plan && version.vms.length > 1) {
      this.plan.allVms = true;
    }
  }

  /** the four tenants always have their button, anything else a scan reports gets one appended */
  private rebuildTenants(): void {
    const extra = Array.from(new Set((this.overview?.rows ?? []).map(it => it.tenant)))
      .filter(it => !KNOWN_TENANTS.includes(it))
      .sort((a, b) => a.localeCompare(b));
    this.tenants = [...KNOWN_TENANTS, ...extra];
  }

  /**
   * The tenants the way we want to read them, the known ones in their own order and whatever else a scan brought
   * behind them. Alphabetically tuw would end up last, and tuw is the one that matters most.
   */
  private inTenantOrder(tenants: string[]): string[] {
    const known = KNOWN_TENANTS.filter(it => tenants.includes(it));
    const extra = tenants.filter(it => !KNOWN_TENANTS.includes(it)).sort((a, b) => a.localeCompare(b));
    return [...known, ...extra];
  }

  tenantCount(tenant: string): number {
    return (this.overview?.rows ?? []).filter(it => it.tenant === tenant).length;
  }

  countOf(status: InsoDockerStatus): number {
    return (this.overview?.rows ?? []).filter(it => it.status === status).length;
  }

  plannedCount(): number {
    return (this.overview?.rows ?? []).filter(it => it.planned).length;
  }

  downCount(): number {
    return this.overview?.rows.filter(it => !it.reporting).length ?? 0;
  }

  /** the css class that gives a row its color, a planned update is calmer than one nobody looked at yet */
  rowClass(row: InsoDockerOverviewRow): string {
    // a vm that does not answer keeps its last known status, the id-down marker says that it is not a fact
    return (row.planned ? 'st-planned' : 'st-' + row.status.toLowerCase()) + (row.reporting ? '' : ' id-down');
  }

  statusClass(status: InsoDockerStatus): string {
    return 'st-' + status.toLowerCase();
  }

  // ── planning an update ─────────────────────────────────────────────────────

  openPlan(row: InsoDockerOverviewRow): void {
    this.plan = {
      image: row.image,
      tenant: row.tenant,
      vm: row.vm,
      fromVersion: row.tag,
      toVersion: row.latestVersion,
      comment: null,
      allVms: false,
      allTenants: false,
    };
    // the backend knows every version of that image that is running, it may suggest a better default
    this.insoDockerService.getSuggestion(row.image, row.tenant, row.vm).subscribe({
      next: res => {
        if (this.plan && this.plan.image === row.image && !this.plan.toVersion) {
          this.plan.toVersion = res.toVersion;
        }
      },
      error: () => { /* the row itself was default enough */ }
    });
  }

  cancelPlan(): void {
    this.plan = null;
  }

  savePlan(): void {
    const draft = this.plan;
    const patchDayId = this.selectedPatchDayId;
    if (!draft || !patchDayId) {
      this.notificationService.error('no patch day selected');
      return;
    }
    const item: InsoDockerPatchDayItem = {
      id: null,
      image: draft.image,
      tenant: draft.allTenants ? null : draft.tenant,
      vm: draft.allVms || draft.allTenants ? null : draft.vm,
      fromVersion: draft.fromVersion,
      toVersion: draft.toVersion,
      status: 'PLANNED',
      comment: draft.comment,
      currentVersions: [],
      vms: [],
      downVms: [],
    };
    this.insoDockerService.createItem(patchDayId, item).subscribe({
      next: () => {
        this.plan = null;
        this.notificationService.success(`planned ${item.image} ${item.toVersion ?? ''}`);
        this.refreshPatchDays(true);
        this.refreshOverview(true);
      },
      error: err => this.notificationService.error('failed to plan the update ' + JSON.stringify(err))
    });
  }

  // ── patch days ─────────────────────────────────────────────────────────────

  selectPatchDay(id: number | null): void {
    this.selectedPatchDayId = id;
    this.patchDayItems = [];
    this.selectedItems.clear();
    this.refreshPatchDayItems(true);
    this.refreshOverview(true);
  }

  selectedPatchDay(): InsoDockerPatchDay | undefined {
    return this.patchDays.find(it => it.id === this.selectedPatchDayId);
  }

  // ── moving items to another patch day ──────────────────────────────────────

  /** every patch day but the one that is open, those are the ones an item can be moved to */
  otherPatchDays(): InsoDockerPatchDay[] {
    return this.patchDays.filter(it => it.id !== this.selectedPatchDayId);
  }

  isItemSelected(item: InsoDockerPatchDayItem): boolean {
    return item.id !== null && this.selectedItems.has(item.id);
  }

  toggleItem(item: InsoDockerPatchDayItem): void {
    if (item.id === null) {
      return;
    }
    if (!this.selectedItems.delete(item.id)) {
      this.selectedItems.add(item.id);
    }
  }

  allItemsSelected(): boolean {
    return this.patchDayItems.length > 0 && this.patchDayItems.every(it => this.isItemSelected(it));
  }

  toggleAllItems(): void {
    const select = !this.allItemsSelected();
    this.selectedItems.clear();
    if (select) {
      this.patchDayItems.forEach(it => it.id !== null && this.selectedItems.add(it.id));
    }
  }

  /** takes the ticked items over to [patchDayId], they keep everything but the patch day they hang on */
  moveItems(patchDayId: number | null): void {
    const itemIds = Array.from(this.selectedItems);
    if (patchDayId === null || !itemIds.length) {
      return;
    }
    this.insoDockerService.moveItems(patchDayId, itemIds).subscribe({
      next: moved => {
        this.moveTargetId = null;
        this.selectedItems.clear();
        this.notificationService.success(`${moved.length} moved to ${this.patchDayName(patchDayId)}`);
        this.refreshPatchDayItems(true);
        this.refreshPatchDays(true);
        this.refreshOverview(true);
      },
      error: err => {
        this.moveTargetId = null;
        this.notificationService.error('failed to move the items ' + JSON.stringify(err));
      }
    });
  }

  patchDayName(id: number): string {
    const patchDay = this.patchDays.find(it => it.id === id);
    return [patchDay?.day, patchDay?.title].filter(it => it).join(' ') || 'patch day ' + id;
  }

  /**
   * What the planned badge of a row says: the patch day that is going to take care of it and the version it takes
   * it to. Which patch day it is matters, "planned" alone leaves one digging through the patch days for it.
   */
  plannedLabel(row: InsoDockerOverviewRow): string {
    const name = row.patchDayId === null ? 'planned' : this.patchDayName(row.patchDayId);
    return [name, row.plannedToVersion].filter(it => it).join(' \u2192 ');
  }

  openItems(): InsoDockerPatchDayItem[] {
    return this.patchDayItems.filter(it => it.status === 'PLANNED');
  }

  closedItems(): InsoDockerPatchDayItem[] {
    return this.patchDayItems.filter(it => it.status !== 'PLANNED');
  }

  /**
   * The patch day as a markdown task list, the shape a gitlab issue wants: whoever gets the issue ticks the
   * updates off there while the scans tick them off here. What is already done or skipped is in it as well,
   * an issue that only lists what is left reads as if that work never happened.
   */
  copyPatchDayMarkdown(patchDay: InsoDockerPatchDay): void {
    this.copyMarkdown(this.patchDayMarkdown(patchDay), 'the patch day');
  }

  private patchDayMarkdown(patchDay: InsoDockerPatchDay): string {
    const title = [patchDay.day, patchDay.title].filter(it => it).join(' ');
    const items = [...this.openItems(), ...this.closedItems()];
    const lines = ['## ' + (title || 'patch day'), ''];
    items.forEach(item => lines.push(this.itemMarkdown(item)));
    if (!items.length) {
      lines.push('nothing planned');
    }
    return lines.join('\n') + '\n';
  }

  private itemMarkdown(item: InsoDockerPatchDayItem): string {
    const tenant = item.tenant ?? 'all tenants';
    const vms = item.vm ?? (item.vms.length ? item.vms.join(', ') : 'all vms');
    const from = item.fromVersion ?? (item.currentVersions.join(', ') || '?');
    const done = item.status === 'PLANNED' ? ' ' : 'x';
    let line = `- [${done}] **${item.image}** \`${from}\` → \`${item.toVersion ?? '?'}\` (${tenant} ${vms})`;
    if (item.status === 'SKIPPED') {
      line += ' _skipped_';
    }
    if (item.comment) {
      line += ' — ' + item.comment;
    }
    return line;
  }

  // ── the other tables as markdown ──────────────────────────────────────

  /**
   * One group of the overview as a markdown table, the same rows and columns the card shows. A patch day is a task
   * list because it is work to be done, a group is a state of things, so here a table it is.
   */
  copyGroupMarkdown(group: OverviewGroup): void {
    const rows = group.rows.map(row => [
      row.image,
      row.tag,
      row.latestVersion ?? '?',
      row.status.toLowerCase() + (row.planned ? ' \u00b7 ' + this.plannedLabel(row) : ''),
      row.tenant,
      row.vm,
      row.containers.join(', '),
      row.composeDirs.join(', '),
    ]);
    const table = this.markdownTable(['image', 'version', 'newest', 'status', 'tenant', 'vm', 'containers', 'compose dir'], rows);
    this.copyMarkdown(this.markdownDocument(group.name, table), group.name);
  }

  /** the image against tenant matrix as a markdown table, one column per tenant as on the screen */
  copyMatrixMarkdown(): void {
    const rows = this.matrix.map(row => [
      row.image,
      ...row.cells.map(cell => cell.versions.map(it => it.tag + (it.vms.length > 1 ? ' \u00d7' + it.vms.length : '')).join(', ') || '\u2013'),
      row.newestVersion ?? '?',
    ]);
    const table = this.markdownTable(['image', ...this.matrixTenants, 'newest'], rows);
    this.copyMarkdown(this.markdownDocument('versions per tenant', table), 'the matrix');
  }

  /** the rules as a markdown table, the answer to "why does this tooling say what it says" */
  copyRulesMarkdown(): void {
    const rows = this.rules.map(rule => [
      rule.image,
      rule.type.toLowerCase(),
      rule.value ?? '',
      rule.comment ?? '',
      [rule.created?.substring(0, 10), rule.createdBy].filter(it => it).join(' '),
      rule.active ? '' : 'inactive',
    ]);
    const table = this.markdownTable(['image', 'type', 'version', 'comment', 'created', ''], rows);
    this.copyMarkdown(this.markdownDocument('inso docker rules', table), 'the rules');
  }

  /** heading, the table and where the numbers come from, an export that lands in an issue should say how old it is */
  private markdownDocument(title: string, table: string[]): string {
    const scan = this.overview?.lastScan;
    const note = scan ? `scan of ${scan.replace('T', ' ').substring(0, 16)}` : 'no scan yet';
    return [`## ${title}`, '', ...table, '', `_${note}_`, ''].join('\n');
  }

  /** a markdown table, pipes in a value are escaped so that a container name cannot break the columns */
  private markdownTable(headers: string[], rows: string[][]): string[] {
    const line = (cells: string[]) => `| ${cells.map(it => (it || '').replace(/\|/g, '\\|')).join(' | ')} |`;
    return [line(headers), line(headers.map(() => '---')), ...rows.map(line)];
  }

  /** the end of every export, the clipboard is what all of them are for */
  private copyMarkdown(markdown: string, what: string): void {
    navigator.clipboard.writeText(markdown).then(
      () => this.notificationService.success(`${what} is in the clipboard as markdown`),
      err => this.notificationService.error(`the clipboard did not take it ${err}`)
    );
  }

  newPatchDayForm(): void {
    this.newPatchDay = {
      id: null,
      day: new Date().toISOString().substring(0, 10),
      title: null,
      closed: false,
      plannedCount: 0,
      doneCount: 0,
      skippedCount: 0,
    };
  }

  cancelPatchDay(): void {
    this.newPatchDay = null;
  }

  savePatchDay(): void {
    const patchDay = this.newPatchDay;
    if (!patchDay) {
      return;
    }
    this.insoDockerService.createPatchDay(patchDay).subscribe({
      next: res => {
        this.newPatchDay = null;
        this.refreshPatchDays(true);
        this.selectPatchDay(res.id);
      },
      error: err => this.notificationService.error('failed to create the patch day ' + JSON.stringify(err))
    });
  }

  togglePatchDayClosed(patchDay: InsoDockerPatchDay): void {
    this.insoDockerService.updatePatchDay({...patchDay, closed: !patchDay.closed}).subscribe({
      next: () => this.refreshPatchDays(true),
      error: err => this.notificationService.error('failed to update the patch day ' + JSON.stringify(err))
    });
  }

  deletePatchDay(patchDay: InsoDockerPatchDay): void {
    if (!patchDay.id) {
      return;
    }
    this.insoDockerService.deletePatchDay(patchDay.id).subscribe({
      next: () => {
        if (this.selectedPatchDayId === patchDay.id) {
          this.selectPatchDay(null);
        }
        this.refreshPatchDays(true);
      },
      error: err => this.notificationService.error('failed to delete the patch day ' + JSON.stringify(err))
    });
  }

  setItemStatus(item: InsoDockerPatchDayItem, status: InsoDockerPatchDayItemStatus): void {
    this.insoDockerService.updateItem({...item, status}).subscribe({
      next: () => {
        this.refreshPatchDayItems(true);
        this.refreshPatchDays(true);
        this.refreshOverview(true);
      },
      error: err => this.notificationService.error('failed to update the item ' + JSON.stringify(err))
    });
  }

  deleteItem(item: InsoDockerPatchDayItem): void {
    if (!item.id) {
      return;
    }
    this.insoDockerService.deleteItem(item.id).subscribe({
      next: () => {
        this.refreshPatchDayItems(true);
        this.refreshPatchDays(true);
        this.refreshOverview(true);
      },
      error: err => this.notificationService.error('failed to delete the item ' + JSON.stringify(err))
    });
  }

  /** the version an item should aim at today, a plan made a month ago still points at the version of that day */
  itemNewest(item: InsoDockerPatchDayItem): string | null {
    const newest = (this.overview?.rows ?? []).find(row => row.image === item.image)?.latestVersion ?? null;
    return newest && item.toVersion && this.isOutdated(item.toVersion, newest) ? newest : null;
  }

  /** takes the newest released version over as the target, one click instead of retyping it */
  bumpItem(item: InsoDockerPatchDayItem, toVersion: string): void {
    this.insoDockerService.updateItem({...item, toVersion}).subscribe({
      next: () => this.refreshPatchDayItems(true),
      error: err => this.notificationService.error('failed to update the item ' + JSON.stringify(err))
    });
  }

  /** what is still running of the versions this item wants to get rid of, that is the shrinking to do list */
  itemLeft(item: InsoDockerPatchDayItem): string[] {
    const toVersion = item.toVersion;
    if (!toVersion) {
      return item.currentVersions;
    }
    return item.currentVersions.filter(it => this.isOutdated(it, toVersion));
  }

  /** the same arithmetic strapi uses to tick an item off, so that 15.7 counts as up to date against 15.7.1 */
  private isOutdated(current: string, target: string): boolean {
    const currentTuple = this.versionTuple(current);
    const targetTuple = this.versionTuple(target);
    if (!currentTuple.length || !targetTuple.length) {
      return false;
    }
    const length = Math.min(currentTuple.length, targetTuple.length);
    for (let i = 0; i < length; i++) {
      if (currentTuple[i] !== targetTuple[i]) {
        return currentTuple[i] < targetTuple[i];
      }
    }
    return false;
  }

  private versionTuple(version: string): number[] {
    const cleaned = version
      .replace(/^[vV]+/, '')
      .replace(/^[a-zA-Z]+[-_]/, '')
      .replace(/-g[0-9a-f]+/, '')
      .replace(/[-_](debian|alpine|ubuntu|plugins|trixie|ce|cuda|apache|fpm).*/i, '');
    return (cleaned.match(/\d+/g) ?? []).map(it => parseInt(it, 10));
  }

  // ── rules, the interactive part the script could not do ────────────────────

  toggleRules(): void {
    this.showRules = !this.showRules;
  }

  /** a cve just came out: everything below the newest version of that image is evil from now on */
  declareEvil(row: InsoDockerOverviewRow): void {
    this.showRules = true;
    this.newRule = {
      id: null,
      image: row.image,
      type: 'CRITICAL_BELOW',
      value: row.latestVersion ?? row.tag,
      comment: null,
      active: true,
    };
  }

  /** what the version field of a rule means, two of the four rule types do not read it at all */
  rulePlaceholder(type: InsoDockerRuleType): string {
    switch (type) {
      case 'CRITICAL_BELOW':
        return 'everything below is evil';
      case 'LATEST_OVERRIDE':
        return 'the newest version there is';
      default:
        return 'not used for this rule';
    }
  }

  /** nobody builds that image any more, so every tag of it is critical no matter what version it carries */
  abandonImage(row: InsoDockerOverviewRow): void {
    this.showRules = true;
    this.newRule = {
      id: null,
      image: row.image,
      type: 'ABANDONED',
      value: null,
      comment: null,
      active: true,
    };
  }

  ignoreImage(row: InsoDockerOverviewRow): void {
    this.showRules = true;
    this.newRule = {
      id: null,
      image: row.image,
      type: 'IGNORE',
      value: null,
      comment: null,
      active: true,
    };
  }

  newRuleForm(): void {
    this.newRule = {
      id: null,
      image: '',
      type: 'CRITICAL_BELOW',
      value: null,
      comment: null,
      active: true,
    };
  }

  cancelRule(): void {
    this.newRule = null;
  }

  saveRule(): void {
    const rule = this.newRule;
    if (!rule || !rule.image) {
      this.notificationService.error('an image is needed for a rule');
      return;
    }
    this.insoDockerService.createRule(rule).subscribe({
      next: () => {
        this.newRule = null;
        this.refreshRules(true);
        this.refreshOverview(true);
      },
      error: err => this.notificationService.error('failed to create the rule ' + JSON.stringify(err))
    });
  }

  toggleRuleActive(rule: InsoDockerRule): void {
    this.insoDockerService.updateRule({...rule, active: !rule.active}).subscribe({
      next: () => {
        this.refreshRules(true);
        this.refreshOverview(true);
      },
      error: err => this.notificationService.error('failed to update the rule ' + JSON.stringify(err))
    });
  }

  deleteRule(rule: InsoDockerRule): void {
    if (!rule.id) {
      return;
    }
    this.insoDockerService.deleteRule(rule.id).subscribe({
      next: () => {
        this.refreshRules(true);
        this.refreshOverview(true);
      },
      error: err => this.notificationService.error('failed to delete the rule ' + JSON.stringify(err))
    });
  }

  // ── plumbing ───────────────────────────────────────────────────────────────

  private setupSse(): void {
    // the events carry no data, they only say that a scan, a lookup or somebody else changed something
    this.sse = new EventSource(this.baseUrlService.getBaseUrl() + '/insoDocker/sse');
    this.sse.onmessage = () => {
      this.zone.run(() => {
        if (this.live) {
          this.refreshOverview(false);
          this.refreshPatchDays(false);
          this.refreshPatchDayItems(false);
        }
      });
    };
  }

  private refreshOverview(reportErrors: boolean): void {
    if (this.overviewPending) {
      return;
    }
    this.overviewPending = true;
    this.insoDockerService.getOverview().subscribe({
      next: res => {
        this.overviewPending = false;
        this.overview = res;
        this.regroup();
      },
      error: err => {
        this.overviewPending = false;
        if (reportErrors) {
          this.notificationService.error('failed to retrieve the docker image versions ' + JSON.stringify(err));
        }
      }
    });
  }

  private refreshPatchDays(reportErrors: boolean): void {
    this.insoDockerService.getPatchDays().subscribe({
      next: res => {
        this.patchDays = res;
        if (this.selectedPatchDayId === null) {
          this.selectedPatchDayId = res.find(it => !it.closed)?.id ?? null;
          this.refreshPatchDayItems(false);
        }
      },
      error: err => {
        if (reportErrors) {
          this.notificationService.error('failed to retrieve the patch days ' + JSON.stringify(err));
        }
      }
    });
  }

  private refreshPatchDayItems(reportErrors: boolean): void {
    const id = this.selectedPatchDayId;
    if (id === null) {
      this.patchDayItems = [];
      return;
    }
    this.insoDockerService.getPatchDay(id).subscribe({
      next: res => {
        if (this.selectedPatchDayId === id) {
          this.patchDayItems = res.items;
          // a tick on an item that is not here anymore must not travel along with the next move
          this.selectedItems = new Set(res.items.filter(it => this.isItemSelected(it)).map(it => it.id as number));
        }
      },
      error: err => {
        if (reportErrors) {
          this.notificationService.error('failed to retrieve the patch day ' + JSON.stringify(err));
        }
      }
    });
  }

  private refreshRules(reportErrors: boolean): void {
    this.insoDockerService.getRules().subscribe({
      next: res => this.rules = res,
      error: err => {
        if (reportErrors) {
          this.notificationService.error('failed to retrieve the rules ' + JSON.stringify(err));
        }
      }
    });
  }

  private matchesFilter(row: InsoDockerOverviewRow): boolean {
    // a planned update is the list one is working on, so not its status decides whether it shows but its own switch
    if (row.planned) {
      if (!this.showPlanned) {
        return false;
      }
    } else if (!row.reporting) {
      if (!this.showDown) {
        return false;
      }
    } else if (!this.visibleStatus[row.status]) {
      return false;
    }
    return this.matchesSearch(row) && this.matchesTenant(row);
  }

  private matchesTenant(row: InsoDockerOverviewRow): boolean {
    return !this.hiddenTenants.has(row.tenant);
  }

  private matchesSearch(row: InsoDockerOverviewRow): boolean {
    const search = this.search.trim().toLowerCase();
    if (!search) {
      return true;
    }
    return [row.image, row.tag, row.vm, row.tenant, row.latestVersion ?? '', ...row.containers, ...row.composeDirs]
      .some(it => it.toLowerCase().includes(search));
  }

  private groupNameOf(row: InsoDockerOverviewRow): string {
    switch (this.groupBy) {
      case 'tenant':
        return row.tenant;
      case 'vmPrefix':
        return row.vmPrefix + '*';
      case 'tenantVmPrefix':
        return `${row.tenant} / ${row.vmPrefix}*`;
      case 'image':
        return row.image;
      default:
        return 'all';
    }
  }
}
